import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { UnitsEdit, UnitsAdd } from "./UnitsAddOrEdit";
import EncryptionUtils from "../../Utils/EncryptionUtils";

export default function AddOrEditUnits({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [unitData, setUnitData] = useState(new UnitsAdd());
  const [locations, setLocations] = useState([]);
  const [locationsDestino, setLocationsDestino] = useState([]);
  const [locationUnit, setLocationUnit] = useState(null);
  const [locationDestinoUnit, setLocationDestinoUnit] = useState(null);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [unidadModelo, setUnidadModelo] = useState([]);

  useEffect(() => {
    if (location.pathname.includes("/Unidades/Editar")) {
      setTitle(t("units.addOrEdit.edit"));
      const locationId = getLocationIdFromPath(location.pathname);
      getUnitById(locationId);
    } else {
      setTitle(t("units.addOrEdit.add"));
    }

    fetchClients();
    fetchMarcas();
  }, [location.pathname, t]);

  useEffect(() => {
    if (title === t("units.addOrEdit.edit") && unitData.idUbicacion !== 0) {
      setLocationUnit({
        idUbicacion: unitData.idUbicacion,
        nombre: unitData.ubicacion,
        descripcion: "",
        ciudad: "",
        ubicacionTipo: "",
      });
    }

    if (title === t("units.addOrEdit.edit") && unitData.idUbicacionDestino !== 0) {
      setLocationDestinoUnit({
        idUbicacion: unitData.idUbicacionDestino,
        nombre: unitData.ubicacionDestino,
        descripcion: "",
        ciudad: "",
        ubicacionTipo: "",
      });
    }
  }, [title, unitData]);

  const getLocationIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getUnitById = async (unitId) => {
    const dataUnit = await GET(`${config.url}/api/Unit/${unitId}`);
    const updateDataUnit = new UnitsEdit(dataUnit);

    setUnitData(updateDataUnit);

    if (
      updateDataUnit.idUbicacion !== 0 &&
      updateDataUnit.idUbicacion !== null &&
      updateDataUnit.idUbicacion !== undefined
    ) {
      const locationData = await GET(
        `${config.url}/api/Location/${updateDataUnit.idUbicacion}`
      );
      fetchLocations(locationData.nombre, 1, 1);
    }

    if (
      updateDataUnit.idUbicacionDestino !== 0 &&
      updateDataUnit.idUbicacionDestino &&
      updateDataUnit.idUbicacionDestino !== undefined
    ) {
      const locationData = await GET(
        `${config.url}/api/Location/${updateDataUnit.idUbicacionDestino}`
      );
      fetchLocationsDestino(locationData.nombre, 1, 1);
    }

    if (updateDataUnit.idMarca !== 0 && updateDataUnit.idMarca !== null)
      fetchUnidadModelo(updateDataUnit.idMarca);
  };

  const fetchLocationsDestino = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocationsDestino(dataLocations.result);
  };

  const fetchLocations = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocations(dataLocations.result);
  };

  const fetchClients = async () => {
    const clientsData = await GET(`${config.url}/api/Client`);
    setClients(clientsData);
  };

  const fetchMarcas = async () => {
    const marcasData = await GET(`${config.url}/api/Marcas`);
    setMarcas(marcasData);
  };

  const fetchUnidadModelo = async (idMarca) => {
    const unidadModeloData = await GET(
      `${config.url}/api/Marcas/GetUnidadModelosByIdMarca`,
      {
        idMarca: idMarca,
      }
    );

    setUnidadModelo(unidadModeloData.unidadModelos);
  };

  const addUnit = async () => {
    const requiredFields = [
      "vin",
      "anio",
      "idUbicacion",
      "idUbicacionDestino",
      "idCliente",
      "idMarca",
      "idModelo",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(unitData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("units.addOrEdit.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const dataUser = EncryptionUtils.getDecryptedData("user");
    const parsedUser = JSON.parse(dataUser);

    unitData.idUsuario = parsedUser.userId;

    await POST(`${config.url}/api/Unit`, unitData);
    navigate("/Unidades");
  };

  const editUnit = async () => {
    const requiredFields = [
      "vin",
      "anio",
      "idUbicacion",
      "idUbicacionDestino",
      "idCliente",
      "idMarca",
      "idModelo",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(unitData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("units.addOrEdit.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await PUT(`${config.url}/api/Unit`, unitData);
    navigate("/Unidades");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    if (title === t("units.addOrEdit.add")) {
      await addUnit();
    } else if (title === t("units.addOrEdit.edit")) {
      await editUnit();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "idMarca") {
      fetchUnidadModelo(value);

      if (unidadModelo.length > 0) {
        setUnitData({
          ...unitData,
          [name]: value,
          idModelo: unidadModelo[0].idUnidadModelo,
        });
      } else {
        setUnitData({
          ...unitData,
          [name]: value,
          idModelo: 0,
        });
      }
    } else {
      setUnitData({
        ...unitData,
        [name]: value,
      });
    }
  };

  const handleInputChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocations([]);
    }
  };

  const handleInputChangeDestino = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocationsDestino(value, 1, 10);
    } else {
      setLocationsDestino([]);
    }
  };

  const handleNumberInput = (e) => {
    const key = e.key;

    if (
      !/^[0-9]$/.test(key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)
    ) {
      e.preventDefault();
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/Unidades"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {title} {t("units.addOrEdit.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            marginTop: 2,
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t("units.addOrEdit.unitInfo")}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("units.vin")}
                  name="vin"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={unitData.vin || ""}
                  inputProps={{ maxLength: 17 }}
                  error={!!errors.vin}
                  helperText={errors.vin}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("units.addOrEdit.year")}
                  name="anio"
                  variant="standard"
                  fullWidth
                  onKeyDown={handleNumberInput}
                  onChange={handleChange}
                  value={unitData.anio || ""}
                  error={!!errors.anio}
                  helperText={errors.anio}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={locations}
                  noOptionsText={t("noOptions")}
                  value={locationUnit}
                  getOptionLabel={(location) =>
                    `${location.nombre} ${location.descripcion} ${
                      location.ciudad ?? ""
                    } ${location.ubicacionTipo ?? ""}`
                  }
                  onChange={(event, value) => {
                    if (value) {
                      setLocationUnit(value);
                      setUnitData({
                        ...unitData,
                        ubicacion: value.nombre,
                        idUbicacion: value.idUbicacion,
                      });
                    } else {
                      setUnitData({
                        ...unitData,
                        ubicacion: "",
                        idUbicacion: 0,
                      });
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option && value && option.idUbicacion === value.idUbicacion
                  }
                  onInputChange={handleInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("units.addOrEdit.originLocation")}
                      variant="standard"
                      fullWidth
                      value={locationUnit}
                      error={!!errors.idUbicacion}
                      helperText={errors.idUbicacion}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={locationsDestino}
                  noOptionsText={t("noOptions")}
                  value={locationDestinoUnit}
                  getOptionLabel={(location) =>
                    `${location.nombre} ${location.descripcion} ${
                      location.ciudad ?? ""
                    } ${location.ubicacionTipo ?? ""}`
                  }
                  onChange={(event, value) => {
                    if (value) {
                      setLocationDestinoUnit(value);
                      setUnitData({
                        ...unitData,
                        ubicacionDestino: value.nombre,
                        idUbicacionDestino: value.idUbicacion,
                      });
                    } else {
                      setUnitData({
                        ...unitData,
                        ubicacionDestino: "",
                        idUbicacionDestino: 0,
                      });
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option &&
                    value &&
                    option.idUbicacionDestino === value.idUbicacionDestino
                  }
                  onInputChange={handleInputChangeDestino}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("units.addOrEdit.destinationLocation")}
                      variant="standard"
                      fullWidth
                      value={locationDestinoUnit}
                      error={!!errors.idUbicacionDestino}
                      helperText={errors.idUbicacionDestino}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t("units.client")}
                  name="idCliente"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={unitData.idCliente || 0}
                  onChange={handleChange}
                  error={!!errors.idCliente}
                  helperText={errors.idCliente}
                >
                  <MenuItem key={0} value={0}>
                    {t("select")}
                  </MenuItem>
                  {clients.map((cliente) => (
                    <MenuItem key={cliente.idCliente} value={cliente.idCliente}>
                      {cliente.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t("units.brand")}
                  name="idMarca"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={unitData.idMarca || 0}
                  onChange={handleChange}
                  error={!!errors.idMarca}
                  helperText={errors.idMarca}
                >
                  <MenuItem key={0} value={0}>
                    {t("select")}
                  </MenuItem>
                  {marcas.map((marca) => (
                    <MenuItem key={marca.idMarca} value={marca.idMarca}>
                      {marca.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t("units.addOrEdit.model")}
                  name="idModelo"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={unitData.idModelo || 0}
                  onChange={handleChange}
                  error={!!errors.idModelo}
                  helperText={errors.idModelo}
                >
                  <MenuItem key={0} value={0}>
                    {t("select")}
                  </MenuItem>
                  {unidadModelo.map((modelo) => (
                    <MenuItem
                      key={modelo.idUnidadModelo}
                      value={modelo.idUnidadModelo}
                    >
                      {modelo.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {t("units.addOrEdit.save")}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
